/**
 * Vague 1 · Phase C.4 · Outbox : queue FIFO d'opérations terrain en attente.
 *
 * Pattern offline-first :
 *  1. L'UI tente une mutation → si online + 2xx : direct.
 *  2. Si `ApiTransportError` (réseau down) : on push dans l'outbox et on
 *     retourne un placeholder optimiste à l'UI.
 *  3. Au retour réseau (`online` event) : `flushOutbox()` rejoue dans
 *     l'ordre. Les ops 2xx sont supprimées, les 4xx non-transitoires sont
 *     marquées `failed` (visible UI), pas de boucle.
 *
 * Opérations FIFO strict pour garder la cohérence métier :
 *  - Si l'agent fait prise → main courante → sortie hors réseau, la
 *    sortie ne doit pas partir AVANT la prise (sinon refus 400 "missing_prise").
 */
import { getDb, type OutboxOp, type OutboxOpKind, type OutboxOpStatus } from "./offline-db";

const LOCAL_UUID = (): string => {
  // crypto.randomUUID dispo partout (HTTPS only · agent.myvictor.fr l'est).
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback Math.random (très improbable d'arriver ici)
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export interface EnqueueInput {
  kind: OutboxOpKind;
  payload: unknown;
  meta?: { vacationId?: string };
}

export async function enqueueOutbox(input: EnqueueInput): Promise<OutboxOp> {
  const op: OutboxOp = {
    id: LOCAL_UUID(),
    kind: input.kind,
    payload: input.payload,
    createdAt: new Date().toISOString(),
    attempts: 0,
    status: "pending",
    meta: input.meta,
  };
  const db = await getDb();
  await db.add("outbox", op);
  return op;
}

export async function listOutbox(): Promise<OutboxOp[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex("outbox", "by-createdAt");
  return all;
}

export async function countOutbox(): Promise<{
  total: number;
  pending: number;
  failed: number;
}> {
  const all = await listOutbox();
  return {
    total: all.length,
    pending: all.filter((o) => o.status === "pending").length,
    failed: all.filter((o) => o.status === "failed").length,
  };
}

export async function countOutboxByVacation(vacationId: string): Promise<number> {
  const all = await listOutbox();
  return all.filter(
    (o) => o.meta?.vacationId === vacationId && o.status !== "failed",
  ).length;
}

export async function deleteOutbox(id: string): Promise<void> {
  const db = await getDb();
  await db.delete("outbox", id);
}

export async function updateOutbox(
  id: string,
  patch: Partial<Pick<OutboxOp, "attempts" | "status" | "lastError">>,
): Promise<void> {
  const db = await getDb();
  const tx = db.transaction("outbox", "readwrite");
  const existing = await tx.store.get(id);
  if (!existing) return;
  await tx.store.put({ ...existing, ...patch });
  await tx.done;
}

export type { OutboxOp, OutboxOpKind, OutboxOpStatus };
